import { useEffect } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import { format } from "date-fns";
import { useDispatch, useSelector } from "react-redux";

import BillingCycle from "./BillingCycle";
import useBillingStyles from "./BillingStyles";
import {
  loadDueCharges,
  loadMoreDueCharges,
  loadMoreStatementInformation,
  loadStatementInformation,
} from "../../store/modules/dueCharges/dueChargesActions";
import { loadMore } from "../../store/modules/pastCharges/pastChargesActions";
import { RootState } from "../../store/store";
import theme from "../../theme";
import { StatementPaymentStatus } from "../../types/types";
import { TransactionClassAttendance } from "../../utils/constants";
import { useIsMaguireEnabled } from "../../utils/maguireHelper";

function DueCharges(): JSX.Element | null {
  const isMaguireEnabled = useIsMaguireEnabled();
  const dispatch = useDispatch();
  const customer_account_id = useSelector((state: RootState) => state.auth.user?.profile.customer_account_id);
  const dueCharges = useSelector((state: RootState) => state.dueCharges.dueChargesState.dueCharges);
  const isDueMoreLoading = useSelector((state: RootState) => state.dueCharges.dueChargesState.isMoreLoading);
  const isDueChargesLoaded = useSelector((state: RootState) => state.dueCharges.dueChargesState.isLoaded);
  const pageIndex = useSelector((state: RootState) => state.dueCharges.dueChargesState.page);
  const isLastPage = useSelector((state: RootState) => state.dueCharges.dueChargesState.isLastPage);
  const pastCharges = useSelector((state: RootState) => state.pastCharges).pastChargesState.pastCharges;
  const isPastChargesLastPage = useSelector((state: RootState) => state.pastCharges).pastChargesState.isLastPage;
  const isPastChargesMoreLoading = useSelector((state: RootState) => state.pastCharges).pastChargesState.isMoreLoading;
  const pageIndexPastCharges = useSelector((state: RootState) => state.pastCharges).pastChargesState.page;

  const statementInformation = useSelector(
    (state: RootState) => state.dueCharges.statementInformationState.statementInformation
  );
  const isStatementMoreLoading = useSelector(
    (state: RootState) => state.dueCharges.statementInformationState.isMoreLoading
  );
  const isStatementLoaded = useSelector((state: RootState) => state.dueCharges.statementInformationState.isLoaded);
  const pageIndexStatement = useSelector((state: RootState) => state.dueCharges.statementInformationState.page);
  const isLastPageStatement = useSelector((state: RootState) => state.dueCharges.statementInformationState.isLastPage);

  const { classes: billingClasses } = useBillingStyles();

  useEffect(() => {
    if (customer_account_id && !isDueChargesLoaded && pageIndex === 1) {
      loadDueCharges(dispatch, customer_account_id, 5, 1);
    }
  }, [customer_account_id, isDueChargesLoaded, pageIndex]);

  const handleShowMore = () => {
    loadMoreDueCharges(dispatch, customer_account_id ?? 0, 5, pageIndex + 1);
  };

  const LoadMoreButton = () => {
    if (isDueMoreLoading === false && isLastPage === false) {
      return (
        <Button onClick={handleShowMore}>
          <strong className="color-accent-2">Load more</strong>
        </Button>
      );
    }
    return null;
  };
  useEffect(() => {
    if (customer_account_id && !isStatementLoaded && pageIndexStatement === 1 && isMaguireEnabled) {
      loadStatementInformation(dispatch, customer_account_id, 5, 1);
    }
  }, [customer_account_id, isStatementLoaded, pageIndexStatement]);
  const handleShowMoreStatement = () => {
    loadMoreStatementInformation(dispatch, customer_account_id ?? 0, 5, pageIndexStatement + 1);
  };
  const LoadMoreButtonCombined = () => {
    const handleClick = () => {
      if (!isLastPageStatement && !isStatementMoreLoading) {
        handleShowMoreStatement();
      }

      if (!isPastChargesLastPage && !isPastChargesMoreLoading) {
        handleShowMorePastCharges();
      }
    };

    if ((!isLastPageStatement && !isStatementMoreLoading) || (!isPastChargesLastPage && !isPastChargesMoreLoading)) {
      return (
        <Grid container justifyContent="center" spacing={2}>
          <Grid item>
            <Button onClick={handleClick}>
              <strong className="color-accent-2">Load more</strong>
            </Button>
          </Grid>
        </Grid>
      );
    }

    return null;
  };

  const handleShowMorePastCharges = () => {
    loadMore(dispatch, customer_account_id ?? 0, 5, pageIndexPastCharges + 1);
  };

  if (!isMaguireEnabled) {
    if (dueCharges && dueCharges.length > 0) {
      return (
        <>
          <h2 className={billingClasses.billingSectionHeader}>Due Charges</h2>
          {dueCharges.map((item) => {
            var totalBookingsAmount = item.transactions
              .filter((t) => t.transactionClassId === TransactionClassAttendance && t.reversalDate === undefined)
              .reduce((a, b) => a + (b.fees ?? 0), 0);
            var totalCCSAndACCSAmount = item.transactions.reduce((a, b) => a + (b.ccs + b.accs), 0);
            var totalOtherFees = item.transactions
              .filter((t) => t.transactionClassId !== TransactionClassAttendance && t.reversalDate === undefined)
              .reduce((a, b) => a + (b.totalDebits ?? 0), 0);
            var credits = item.transactions.reduce((a, b) => a + (b.parentFeeWaiver ?? 0), 0);
            var totalOutOfPocketPeriod =
              totalBookingsAmount - Math.abs(totalCCSAndACCSAmount) + totalOtherFees + credits;
            return (
              <>
                <BillingCycle
                  key={item.billingCycleId}
                  billingCycleId={item.billingCycleId}
                  customerAccountId={customer_account_id as number}
                  billingPeriodEnd={item.billingPeriodEnd}
                  totalAmount={totalOutOfPocketPeriod ?? 0}
                  billingCycleStatus={
                    item.isStatementCleared ? "" : item.isOverDue ? "OverDue" : item.isDue ? "Due" : ""
                  }
                  statusColor={
                    item.isStatementCleared
                      ? theme.palette.success.light
                      : item.isOverDue
                      ? theme.palette.error.light
                      : item.isDue
                      ? theme.palette.warning.light
                      : theme.palette.info.light
                  }
                  totalBookingsAmount={totalBookingsAmount}
                  totalCCSAndACCSAmount={totalCCSAndACCSAmount}
                  totalOtherFees={totalOtherFees}
                  totalNumberOfBookings={item.transactions.length}
                  transactions={item.transactions}
                ></BillingCycle>
                <Box paddingTop={1} />
              </>
            );
          })}
          <Grid container justifyContent="center">
            <LoadMoreButton />
          </Grid>
          <Box marginBottom={3} />
        </>
      );
    }
  } else {
    const hasPastCharges = pastCharges && pastCharges.length > 0;
    const hasStatementInformation = statementInformation && statementInformation.length > 0;

    if (hasPastCharges || hasStatementInformation) {
      return (
        <>
          <h2 className={billingClasses.billingSectionHeader}>Statement History</h2>

          {hasStatementInformation &&
            statementInformation.map((item) => {
              const fromDate = format(item.fromDate.toDate(), "dd MMM");
              const toDate = format(item.toDate.toDate(), "dd MMM yyyy");
              const formattedDateRange = `${fromDate} - ${toDate}`;
              const filteredTransactions = item.statementInformationDetail.filter((tx) => {
                if (tx.transactionClassId === TransactionClassAttendance) {
                  return (
                    tx.transactionDate &&
                    tx.transactionDate.isAfter(item.fromDate.subtract(1, "day")) &&
                    tx.transactionDate.isBefore(item.toDate.add(1, "day"))
                  );
                }
                return true;
              });

              return (
                <>
                  <BillingCycle
                    key={`stmt-${item.billingCycleId}`}
                    billingCycleId={item.billingCycleId}
                    customerAccountId={customer_account_id as number}
                    billingPeriodEnd={formattedDateRange}
                    totalAmount={item.totalAmount ?? 0}
                    billingCycleStatus={
                      item.paymentStatusId === StatementPaymentStatus.OverDue
                        ? "OverDue"
                        : item.paymentStatusId === StatementPaymentStatus.Due
                        ? "Due"
                        : ""
                    }
                    statusColor={
                      item.paymentStatusId === StatementPaymentStatus.Paid
                        ? theme.palette.success.light
                        : item.paymentStatusId === StatementPaymentStatus.OverDue
                        ? theme.palette.error.light
                        : item.paymentStatusId === StatementPaymentStatus.Due
                        ? theme.palette.warning.light
                        : theme.palette.info.light
                    }
                    totalBookingsAmount={item.bookingCharges ?? 0}
                    totalCCSAndACCSAmount={(item.ccs ?? 0) + (item.accs ?? 0)}
                    totalOtherFees={item.otherCharges ?? 0}
                    totalNumberOfBookings={filteredTransactions.length}
                    debtCategoryId={item.debtCategoryId}
                    statementId={item.statementId}
                    transactions={filteredTransactions}
                    isWeeklyStatement={true}
                  />
                  <Box paddingTop={1} />
                </>
              );
            })}
          {hasPastCharges &&
            pastCharges.map((item) => (
              <>
                <BillingCycle
                  key={`due-${item.billingCycleId}`}
                  billingCycleId={item.billingCycleId}
                  customerAccountId={customer_account_id as number}
                  billingPeriodEnd={`${format(item.billingPeriodStart.toDate(), "dd MMM")} - ${format(
                    item.billingPeriodEnd.toDate(),
                    "dd MMM yyyy"
                  )}`}
                  totalAmount={item.totalAmount ?? 0}
                  billingCycleStatus={
                    item.isStatementCleared ? "" : item.isOverDue ? "OverDue" : item.isDue ? "Due" : ""
                  }
                  statusColor={
                    item.isStatementCleared
                      ? theme.palette.success.light
                      : item.isOverDue
                      ? theme.palette.error.light
                      : item.isDue
                      ? theme.palette.warning.light
                      : theme.palette.info.light
                  }
                  totalBookingsAmount={item.transactions
                    .filter((t) => t.transactionClassId === TransactionClassAttendance && t.reversalDate === undefined)
                    .reduce((a, b) => a + (b.fees ?? 0), 0)}
                  totalCCSAndACCSAmount={item.transactions.reduce((a, b) => a + (b.ccs + b.accs), 0)}
                  totalOtherFees={item.transactions
                    .filter((t) => t.transactionClassId !== TransactionClassAttendance && t.reversalDate === undefined)
                    .reduce((a, b) => a + (b.totalDebits ?? 0), 0)}
                  totalNumberOfBookings={item.transactions.length}
                  transactions={item.transactions}
                  isWeeklyStatement={false}
                />
                <Box paddingTop={1} />
              </>
            ))}
          <LoadMoreButtonCombined />
          <Box marginBottom={3} />
        </>
      );
    }
  }
  return null;
}

export default DueCharges;
